/**
 * SEO configuration for the routes that still use this (older) head manager: the authenticated ERP
 * shell's `noindex`, the app 404, and the two enquiry pages (both `noindex`).
 *
 * The canonical origin is NOT declared here any more. It has exactly one home — `lib/canonicalHost`
 * — because this file used to say bio.airmax.co.in while the marketing site's own seo module said
 * greenbaybio.com, and the disagreement shipped: every page served a canonical link pointing at the
 * other host. Re-export it so existing importers keep working, but never redefine it.
 */
import { SITE_URL as CANONICAL_ORIGIN } from '../canonicalHost';

/** Canonical, absolute origin. No trailing slash. Used for canonical + OG URLs + sitemap. */
export const SITE_URL = CANONICAL_ORIGIN;

/** Full legal brand — used as og:site_name and the title suffix. */
export const SITE_NAME = 'GreenbayBio';
/** Short brand — used for the PWA short_name and compact contexts. */
export const SITE_SHORT = 'GreenbayBio';

/** Default meta description (~155 chars) used when a page supplies none. */
export const DEFAULT_DESCRIPTION =
 'Bio-fertilizers, bio-stimulants and crop-care that work with nature — restoring soil biology and lifting yield, quality and stress-resilience for Indian farmers.';

/** Default social-share image (og:image / twitter:image). `/og/default.webp` is the real 1200×630
 *  card that already ships in public/ — it was sitting unused while every share fell back to the
 *  512×304 logo, which social platforms crop or reject. */
export const DEFAULT_OG_IMAGE = '/og/default.webp';

/** Organisation contact facts (mirrors site/shared.tsx CONTACT). */
export const ORG = {
  name: SITE_NAME,
  legalName: 'Greenbay Biotech International Pvt Ltd.',
  email: 'support@greenbaybio.com',
  telephone: '+91-33-4064-6022',
  logo: '/logo.png',
  address: {
    streetAddress: '2B, Grant Lane, 3rd Floor, Room No. 304',
    addressLocality: 'Kolkata',
    addressRegion: 'West Bengal',
    postalCode: '700012',
    addressCountry: 'IN',
  },
};

/** Resolve a path (or already-absolute URL) to an absolute URL on the canonical origin. */
export function absUrl(pathOrUrl?: string): string {
  if (!pathOrUrl) return `${SITE_URL}/`;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return SITE_URL + (pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`);
}

/** Site-wide Organization node (also emitted statically in index.html). */
export const organizationJsonLd = {
 '@context': 'https://schema.org',
 '@type': 'Organization',
  name: ORG.name,
  legalName: ORG.legalName,
  url: `${SITE_URL}/`,
  logo: absUrl(ORG.logo),
  image: absUrl(DEFAULT_OG_IMAGE),
  description: DEFAULT_DESCRIPTION,
  email: ORG.email,
  telephone: ORG.telephone,
  address: {
   '@type': 'PostalAddress',
    streetAddress: ORG.address.streetAddress,
    addressLocality: ORG.address.addressLocality,
    addressRegion: ORG.address.addressRegion,
    postalCode: ORG.address.postalCode,
    addressCountry: ORG.address.addressCountry,
  },
};

/** WebSite node with a sitelinks-searchbox action pointing at the public catalogue. */
export const websiteJsonLd = {
 '@context': 'https://schema.org',
 '@type': 'WebSite',
  name: ORG.name,
  url: `${SITE_URL}/`,
  potentialAction: {
   '@type': 'SearchAction',
    target: {
     '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/catalog?q={search_term_string}`,
    },
   'query-input': 'required name=search_term_string',
  },
};
